/** @format */

let firstShowing = {};

import template from './gb-team-scoring-widget.html';
import './gb-team-scoring-widget.scss';

export const GbTeamScoringWidgetComponent = {
    template,
    require: {
        gbApp: '^^',
        gbPresenter: '^^',
        gbWidget: '^^',
    },
    controller: class GbTeamScoringWidgetComponent {
        constructor(
            $log,
            $gbTeamScore,
            $interval,
            $ccColors,
            $gbTeamScoringProjectorWidget,
            $scope,
            $ccToast,
            $window,
            $ccRealtime,
            $q
        ) {
            'ngInject';

            this.$log = $log;
            this.$gbTeamScore = $gbTeamScore;
            this.$interval = $interval;
            this.$ccColors = $ccColors;
            this.$gbTeamScoringProjectorWidget = $gbTeamScoringProjectorWidget;
            this.$scope = $scope;
            this.$ccToast = $ccToast;
            this.$window = $window;
            this.$ccRealtime = $ccRealtime;
            this.$q = $q;

            this.defaultPageSize = 5;
            this.defaultColumnsSize = 1;
            this.firstTableShowingIndex = 0;
            this.defaultPageShowTime = 10;
            this.teamScoreInterval = 30000; // 30 seconds
            this.teamScorePusher = []; //it will be used to store the uuid of the team score tha needs to be updated
            this.unbindTeamScoreListener = null;

            this.handleVisibilityChangeFunction;
        }

        /**
         * filtering the hidden team scores
         * @returns
         */
        getTeamScores() {
            return this.$gbTeamScore.cache.query({ auction: this.gbApp.getAuctionUrl() }).filter((team) => {
                return (
                    this.gbWidget &&
                    this.gbWidget.widget &&
                    this.gbWidget.widget.hidden_teams &&
                    !this.gbWidget.widget.hidden_teams.includes(team.uuid)
                );
            });
        }

        setColors() {
            this.tablePrimaryColorContrast = this.$ccColors.getContrast(this.gbWidget.widget.table_primary_color);
            this.tableAccentContrastColor = this.$ccColors.getContrast(this.gbWidget.widget.table_accent_color);
        }

        getRowStyles() {
            let height = `${100 / this.getPageSize()}%`;

            return {
                height: height,
            };
        }

        getPageSize() {
            let widget = this.gbWidget.widget;

            return (widget && widget.rows * widget.columns) || this.defaultPageSize * this.defaultColumnsSize;
        }

        getPageShowTime() {
            let widget = this.gbWidget.widget;

            let seconds = (widget && widget.slide_interval) || this.defaultPageShowTime;
            let ms = seconds * 1000;

            return ms;
        }

        cancelPageRotation() {
            if (this.pageRotationInterval) {
                this.$interval.cancel(this.pageRotationInterval);
            }
        }

        getCachedFirstTableShowingIndex() {
            return firstShowing[this.gbWidget.widget.uuid];
        }

        setCachedFirstTableShowingIndex(newIndex) {
            firstShowing[this.gbWidget.widget.uuid] = newIndex;
        }

        startPageRotation() {
            this.cancelPageRotation();

            if (this.getCachedFirstTableShowingIndex()) {
                this.firstTableShowingIndex = this.getCachedFirstTableShowingIndex();
            }

            this.pageRotationInterval = this.$interval(() => {
                let newIndex = this.firstTableShowingIndex + this.getPageSize();
                if (newIndex >= this.getTeamScores().length) {
                    newIndex = 0;
                }

                this.firstTableShowingIndex = newIndex;
                this.setCachedFirstTableShowingIndex(newIndex);
            }, this.getPageShowTime());
        }

        $onInit() {
            this.setColors();
            this.startPageRotation();
            this.syncTeamScore();
            this.setVisibilityChangeWatcher();
            this.setTeamScoreListener();
            this.initTeamScoreInterval();
        }

        syncTeamScore() {
            return this.$gbTeamScore
                .query({ auction: this.gbApp.getAuctionUrl() })
                .$promise.then((data) => {
                    this.$log.info('Synched Team Score', data);
                    this.$gbTeamScore.cache.push(data);
                })
                .catch((err) => {
                    this.$ccToast.error(err);
                    throw err;
                });
        }

        /**
         * this function will be called when the tab is visible or get focus again
         * Because it could miss some team score sync.
         * */
        setVisibilityChangeWatcher() {
            this.handleVisibilityChangeFunction = () => {
                if (!document.hidden) {
                    this.syncTeamScore();
                }
            };
            this.$window.document.addEventListener('visibilitychange', this.handleVisibilityChangeFunction, false);
        }

        $onDestroy() {
            this.cancelPageRotation();
            if (this.handleVisibilityChangeFunction) {
                this.$window.document.removeEventListener('visibilitychange', this.handleVisibilityChangeFunction);
            }

            if (this.unbindTeamScoreListener) {
                if (typeof this.unbindTeamScoreListener === 'function') {
                    this.$log.info('Unbinding team score listener');
                    this.unbindTeamScoreListener();
                }
                if (Array.isArray(this.unbindTeamScoreListener)) {
                    for (let unbind of this.unbindTeamScoreListener) {
                        this.$log.info('Unbinding team score listener');
                        unbind();
                    }
                }
            }

            if (this.initTeamScoreIntervalCancel) {
                this.$interval.cancel(this.initTeamScoreIntervalCancel);
            }
        }

        setTeamScoreListener() {
            this.unbindTeamScoreListener = this.$ccRealtime.on('teamscore.sync', (ev, data) => {
                this.$log.info('Getting a new team score sync', ev, data);
                if (!this.teamScorePusher.includes(data.uuid)) {
                    this.teamScorePusher.push(data.uuid);
                }
            });
        }

        initTeamScoreInterval() {
            this.initTeamScoreIntervalCancel = this.$interval(() => {
                if (Array.isArray(this.teamScorePusher) && this.teamScorePusher.length > 0) {
                    let promises = [];

                    for (let uuid of this.teamScorePusher) {
                        promises.push(
                            this.$gbTeamScore.get({
                                auction: this.gbApp.getAuctionUrl(),
                                uuid: uuid,
                            }).$promise
                        );
                    }

                    this.teamScorePusher.length = 0;

                    this.$q
                        .all(promises)
                        .then((data) => {
                            this.$gbTeamScore.cache.push(data);
                        })
                        .catch((err) => {
                            this.$ccToast.error(err);
                        });
                }
            }, this.teamScoreInterval);
        }

        getColumns() {
            let widget = this.gbWidget.widget;

            return (widget && widget.columns) || this.defaultColumnsSize;
        }

        getRows() {
            let widget = this.gbWidget.widget;

            return (widget && widget.rows) || this.defaultPageSize;
        }

        getTeamScoringStyle() {
            const columns = this.getColumns();
            let columnsWith = `${(100 / columns).toFixed(2)}% `;
            let gridTemplateColumns = '';
            for (let i = 0; i < columns; i++) {
                gridTemplateColumns += columnsWith;
            }
            const gridAutoRows = `${(100 / this.getRows()).toFixed(2)}%`;
            return {
                display: 'grid',
                'grid-template-columns': `${gridTemplateColumns}`,
                'grid-auto-rows': `${gridAutoRows}`,
            };
        }
    },
};
